import { forwardRef } from "react";

// A real <video> element in the server HTML, laid exactly over the existing
// thumbnail image. Google's video indexing needs a <video> on the watch page
// at load; before this the element only existed after a click, so Search
// Console reported "no video detected" on all 18 video pages (17 Oct 2026).
//
// - The thumbnail <img> stays in the flow and sets the size, so the layout
//   does not move. The video sits on top (absolute, inset-0) and shows the same
//   image as its poster with the same object-fit, so the page looks the same.
// - preload="none": no video bytes until someone presses play.
// - While idle the native controls are hidden (.rh-video-idle in styles.css),
//   the element takes no clicks and is out of the tab order, so the page's own
//   gold play button still does what it did. Pass active once it is playing.
type PosterVideoProps = {
  src: string;
  poster: string;
  title: string;
  active?: boolean;
  fit?: "contain" | "cover";
  className?: string;
};

export const PosterVideo = forwardRef<HTMLVideoElement, PosterVideoProps>(function PosterVideo(
  { src, poster, title, active = false, fit = "contain", className = "" },
  ref,
) {
  return (
    <video
      ref={ref}
      src={src}
      poster={poster}
      title={title}
      preload="none"
      controls
      playsInline
      tabIndex={active ? undefined : -1}
      data-state={active ? "active" : "idle"}
      className={`absolute inset-0 h-full w-full ${fit === "cover" ? "object-cover" : "object-contain"} ${
        active ? "" : "rh-video-idle pointer-events-none"
      } ${className}`}
    />
  );
});
